import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CatalogVoice } from "@/utils/voiceCatalog";

type PreviewVoice = Pick<CatalogVoice, "id" | "sample_audio_url" | "updated_at">;
type SampleResponse = { signed_url: string; expires_in: number };
type PreviewState = { voiceId: string | null; status: "idle" | "loading" | "playing"; error: string | null };
const idle: PreviewState = { voiceId: null, status: "idle", error: null };

/** Signs and plays existing catalog files only. Never calls a generation endpoint. */
export function useSavedVoicePreview() {
  const [state, setState] = useState<PreviewState>(idle);
  const sequence = useRef(0);
  const activeId = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlCache = useRef(new Map<string, { url: string; expires: number }>());

  const cancel = useCallback(() => {
    sequence.current += 1;
    activeId.current = null;
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
  }, []);
  const stop = useCallback(() => { cancel(); setState(idle); }, [cancel]);
  // Activity hides this pane without discarding state. Clear the old playback
  // indicator when effects reconnect, while cancelling audio on hide/unmount.
  useEffect(() => { setState(idle); return cancel; }, [cancel]);

  const playOrStop = useCallback(async (voice: PreviewVoice) => {
    if (activeId.current === voice.id) { stop(); return; }
    stop();
    if (!voice.sample_audio_url) {
      setState({ ...idle, error: "This voice has no saved preview yet." });
      return;
    }
    const ticket = sequence.current;
    activeId.current = voice.id;
    setState({ voiceId: voice.id, status: "loading", error: null });
    const cacheKey = `${voice.id}:${voice.updated_at}:${voice.sample_audio_url}`;
    try {
      let cached = urlCache.current.get(cacheKey);
      if (!cached || cached.expires <= Date.now()) {
        const { data, error } = await supabase.functions.invoke<SampleResponse>("voice-sample", { body: { voice_id: voice.id } });
        if (ticket !== sequence.current) return;
        if (error || !data?.signed_url?.trim()) throw new Error("Saved sample unavailable");
        cached = { url: data.signed_url.trim(), expires: Date.now() + Math.max(0, (Number(data.expires_in) || 0) - 30) * 1000 };
        urlCache.current.set(cacheKey, cached);
      }
      if (ticket !== sequence.current) return;
      const audio = new Audio(cached.url);
      audio.preload = "auto";
      const fail = () => {
        if (ticket !== sequence.current) return;
        urlCache.current.delete(cacheKey);
        cancel();
        setState({ ...idle, error: "The saved preview could not be played. Please try again. No new audio was generated." });
      };
      audio.onended = () => { if (ticket === sequence.current) stop(); };
      audio.onerror = fail;
      audioRef.current = audio;
      await audio.play();
      if (ticket !== sequence.current) { audio.pause(); return; }
      setState({ voiceId: voice.id, status: "playing", error: null });
    } catch {
      if (ticket !== sequence.current) return;
      urlCache.current.delete(cacheKey);
      cancel();
      setState({ ...idle, error: "The saved preview could not be started. Please try again. No new audio was generated." });
    }
  }, [cancel, stop]);
  return { ...state, playOrStop, stop };
}
